/**
 * @fileoverview Global auth session store.
 * @description Holds the signed-in user, their access token, and a derived isAuthenticated flag,
 * both hydrated from storage on creation. Signing in is a two-step sequence rather than one atomic
 * action, since the access token has to be stored (and usable by attachAuthToken) before the
 * follow-up GET /identity/me call that fetches the user's profile can even authenticate — see
 * useVerifyTwoFactor.ts, the only caller: setAccessToken first, then setUser once the profile
 * fetch resolves (isAuthenticated only flips true here, not on setAccessToken alone, since nothing
 * should treat a user as signed in before their profile is known). setAccessToken alone is also
 * reused by the response interceptor's silent-refresh flow. logout clears everything locally,
 * every cached query included (so a cached result never outlives its session, even on a forced
 * logout), and only locally — it does not call the backend's /identity/logout itself, since a
 * caller may want that request to fire-and-forget even when it fails (see features/auth's useLogout). There is no
 * refresh-token handling here at all: the identity backend issues it as an httpOnly cookie, so
 * this app's JS never holds or persists it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { create } from 'zustand'

import {
  clearStoredAccessToken,
  clearStoredUser,
  getStoredAccessToken,
  getStoredUser,
  setStoredAccessToken,
  setStoredUser,
} from '@/shared/lib/auth/token-storage.utils'
import { queryClient } from '@/shared/lib/query-client'

import type { AuthUser } from '@/shared/lib/auth/auth.types'

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  isAuthenticated: boolean
  setAccessToken: (accessToken: string) => void
  setUser: (user: AuthUser) => void
  clearMustChangePassword: () => void
  logout: () => void
}

/**
 * @description Zustand store for the current auth session.
 * @returns {AuthState} The current session and its mutators.
 */
export const useAuthStore = create<AuthState>((set) => ({
  user: getStoredUser(),
  accessToken: getStoredAccessToken(),
  isAuthenticated: Boolean(getStoredAccessToken()),
  setAccessToken: (accessToken) => {
    setStoredAccessToken(accessToken)
    set({ accessToken })
  },
  setUser: (user) => {
    setStoredUser(user)
    set({ user, isAuthenticated: true })
  },
  clearMustChangePassword: () => {
    set((state) => {
      if (!state.user) {
        return state
      }

      const user = { ...state.user, mustChangePassword: false }
      setStoredUser(user)
      return { user }
    })
  },
  logout: () => {
    clearStoredAccessToken()
    clearStoredUser()
    queryClient.clear()
    set({ user: null, accessToken: null, isAuthenticated: false })
  },
}))
